<template>
    <v-container class="py-16">
        <template v-if="currentPage === pages.viewFaqModules">
            <FaqModules
                :faqModules="faqModules"
                :faqModulesNotEmpty="faqModulesNotEmpty"
                :fetchingFaqModules="fetchingFaqModules"
                :savingFaqModule="savingModule"
                :deletingFaqModule="deletingFaqModule"
                :updatingFaqModules="updatingFaqModules"
                @update:faqModules="updateFaqModules($event)"
                @createFaqModuleClicked="goTo.createFaqModule()"
                @editFaqModuleClicked="goTo.editFaqModule($event)"
                @deleteFaqModuleClicked="onDeleteModuleClicked($event)"
            />
        </template>

        <v-row
            v-else-if="
                currentPage === pages.createFaqModule ||
                currentPage === pages.editFaqModule
            "
        >
            <v-col cols="12">
                <CreateEditFaqModule
                    :mode="createEditMode"
                    :module="moduleOfInterest"
                    :savingModule="savingModule"
                    :fieldDefaults="fieldDefaults"
                    @goBackClicked="goTo.viewFaqModules()"
                    @saveModuleClicked="onSaveModuleClicked($event)"
                />
            </v-col>
        </v-row>

        <v-snackbar
            v-model="snackbarOpen"
            :timeout="3000"
            top
            :color="snackbarColor"
        >
            <div class="d-flex justify-space-between align-center">
                <span>{{ snackbarText }}</span>
                <v-btn icon small @click="snackbarOpen = false"
                    ><v-icon>mdi-close</v-icon></v-btn
                >
            </div>
        </v-snackbar>

        <v-dialog
            v-if="!!faqModuleToDelete"
            v-model="deleteDialogOpen"
            v-bind="$attrs"
            max-width="800"
        >
            <v-card>
                <v-card-title class="justify-space-between">
                    <span class="primary--text">{{ $t('deleteFaqModuleMsg') }}</span>
                    <v-tooltip top>
                        <template #activator="{ on, attrs }">
                            <v-btn
                                icon
                                @click="deleteDialogOpen = false"
                                v-on="on"
                                v-bind="attrs"
                                ><v-icon>mdi-close</v-icon></v-btn
                            >
                        </template>
                        <span>{{ $t('closeMsg') }}</span>
                    </v-tooltip>
                </v-card-title>
                <v-card-text class="text-body-1">
                    {{  $t('deleteFaqModuleConfirmationMsg') }}
                    <v-card outlined class="pa-2 mt-2">
                        <span v-html="faqModuleToDelete.ui_content"></span>
                    </v-card>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn
                        class="text-none"
                        color="primary"
                        depressed
                        @click="deleteDialogOpen = false"
                    >
                        {{ $t('cancelMsg') }}
                        <v-icon right>mdi-close</v-icon>
                    </v-btn>
                    <v-btn
                        class="text-none"
                        color="error"
                        depressed
                        @click="deleteFaqModule"
                    >
                        {{ $t('deleteMsg') }}
                        <v-icon right>mdi-delete</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import LoadingArea from "@/components/Utils/LoadingArea.vue";
import CreateEditFaqModule from "./CreateEditFaqModule.vue";
import FaqModules from "./FaqModules.vue";
import apiCall from "@/utils/api.js";
import Draggable from "vuedraggable";

const pages = {
    viewFaqModules: 0,
    createFaqModule: 1,
    editFaqModule: 2,
};

export default {
    components: {
        CreateEditFaqModule,
        Draggable,
        LoadingArea,
        FaqModules,
    },
    data() {
        return {
            fetchingFaqModules: false,
            faqModules: [],
            moduleOfInterest: null,
            currentPage: pages.viewFaqModules,
            title: "FAQ Module",
            breadcrumbs: this.getBreadcrumbs(),
            visibleBreadcrumbs: [],
            goTo: this.getGoTo(),
            createEditMode: "create",
            savingModule: false,
            snackbarText: "",
            snackbarOpen: false,
            snackbarColor: "",
            pages,
            directions: {
                up: "up",
                down: "down",
            },
            updatingFaqModules: false,
            deletingFaqModule: false,
            deleteDialogOpen: false,
            faqModuleToDelete: null,
            fieldDefaults: {
                moduleName: `${this.$t('typeModuleNameHere')}...`,
                question: `${this.$t('typeQuestionHere')}...`,
                response: `${this.$t('typeResponseHere')}...`,
            },
        };
    },
    computed: {
        faqModulesNotEmpty() {
            return this.faqModules.length > 0;
        },
        tableLoading() {
            return this.fetchingFaqModules || this.updatingFaqModules;
        },
    },
    methods: {
        async fetchModules() {
            this.fetchingFaqModules = true;
            try {
                let faqModules = await apiCall({
                    url: "/api/faq",
                    method: "GET",
                });
                // faqModules = this.sortModules(faqModules);
                // this.setExtraPropertiesOnModules(faqModules);
                this.faqModules = faqModules;
            } catch (error) {
                console.log(error);
                this.showErrorSnackbar();
            } finally {
                this.fetchingFaqModules = false;
            }
        },

        setExtraPropertiesOnModules(modules) {
            modules.forEach((faqModule) => {
                faqModule.questions.forEach((question) => {
                    question.removed = false;
                    question.new = false;
                });
            });
        },

        sortModules(modules) {
            return modules.sort((a, b) => {
                if (a.order < b.order) {
                    return -1;
                } else if (a.order > b.order) {
                    return 1;
                }
                return 0;
            });
        },

        getEmptyFaqModule() {
            return {
                order: null,
                name: "",
                ui_content: this.fieldDefaults.moduleName,
                questions: [],
            };
        },

        getBreadcrumbs() {
            const onBreadcrumbClicked = (fn) => {
                return (index) => {
                    const isLastItem =
                        index + 1 === this.visibleBreadcrumbs.length;
                    if (isLastItem) {
                        return;
                    }
                    fn();
                };
            };
            const breadcrumbs = {
                dashboard: {
                    text: "Dashboard",
                    onClick: onBreadcrumbClicked(() => {
                        this.goTo.viewFaqModules();
                    }),
                },
                createFaqModule: {
                    text: "Create FAQ Module",
                    onClick: onBreadcrumbClicked(() => {}),
                },
                editFaqModule: {
                    text: "Edit FAQ Module",
                    onClick: onBreadcrumbClicked(() => {}),
                },
            };

            return breadcrumbs;
        },

        getGoTo() {
            const instance = this;
            return {
                viewFaqModules() {
                    instance.currentPage = pages.viewFaqModules;
                    instance.title = "FAQ Modules";
                    instance.visibleBreadcrumbs = [
                        instance.breadcrumbs.dashboard,
                    ];
                },
                createFaqModule() {
                    instance.moduleOfInterest = instance.getEmptyFaqModule();
                    instance.createEditMode = "create";
                    instance.title = "Create FAQ Module";
                    instance.currentPage = pages.createFaqModule;
                    instance.visibleBreadcrumbs = [
                        instance.breadcrumbs.dashboard,
                        instance.breadcrumbs.createFaqModule,
                    ];
                },
                editFaqModule(module) {
                    const moduleCopy = {...module}
                    instance.moduleOfInterest = moduleCopy;
                    instance.createEditMode = "edit";
                    instance.title = "Edit FAQ Module";
                    instance.currentPage = pages.editFaqModule;
                    instance.visibleBreadcrumbs = [
                        instance.breadcrumbs.dashboard,
                        instance.breadcrumbs.editFaqModule,
                    ];
                },
            };
        },

        async onSaveModuleClicked({ mode, module }) {
            let url = "";
            let method = "";
            let snackbarText = "";
            if (mode === "create") {
                url = "/api/faq";
                method = "POST";
                snackbarText = "Created";
            } else if (mode === "edit") {
                url = `/api/faq/${module.id}`;
                method = "PUT";
                snackbarText = "Updated";
            }
            this.savingModule = true;
            try {
                const updatedModule = await apiCall({
                    url,
                    data: { module },
                    method,
                });
                if (mode === "create") {
                    this.goTo.viewFaqModules();
                }
                this.fetchModules();
                this.snackbarOpen = true;
                this.snackbarColor = "success";
                this.snackbarText = `FAQ Module ${snackbarText}`;
            } catch (error) {
                console.log(error);
                this.showErrorSnackbar();
            } finally {
                this.savingModule = false;
            }
        },

        isLastItemInList(index, list) {
            return index + 1 === list.length;
        },

        onMoveFaqModuleClicked({ index, direction }) {
            let firstItem = null;
            let secondItem = null;
            let startIndex = null;
            if (direction === this.directions.up) {
                const isFirstItem = index === 0;
                if (isFirstItem) {
                    return;
                }
                startIndex = index - 1;
                firstItem = this.faqModules[index];
                secondItem = this.faqModules[index - 1];
            } else if (direction === this.directions.down) {
                if (this.isLastItemInList(index, this.faqModules)) {
                    return;
                }
                startIndex = index;
                firstItem = this.faqModules[index + 1];
                secondItem = this.faqModules[index];
            }
            this.faqModules.splice(startIndex, 2, firstItem, secondItem);
        },

        async updateFaqModules(faqModulesToUpdate) {
            this.updatingFaqModules = true;
            try {
                const faqModules = await apiCall({
                    url: "/api/faq/update-modules",
                    data: { modules: faqModulesToUpdate },
                    method: "POST",
                });
                this.faqModules = faqModules;
                this.snackbarOpen = true;
                this.snackbarColor = "success";
                this.snackbarText = this.$t('faqModulesUpdatedMsg');
            } catch (error) {
                console.log(error);
                this.showErrorSnackbar();
            } finally {
                this.updatingFaqModules = false;
            }
        },

        onDeleteModuleClicked(faqModule) {
            this.faqModuleToDelete = faqModule;
            this.deleteDialogOpen = true;
        },

        async deleteFaqModule() {
            this.deleteDialogOpen = false;
            this.deletingFaqModule = true;
            try {
                const faqModules = await apiCall({
                    url: `/api/faq/${this.faqModuleToDelete.id}`,
                    method: "DELETE",
                });
                this.faqModules = faqModules;
                this.snackbarOpen = true;
                this.snackbarColor = "success";
                this.snackbarText = "FAQ Module Deleted";
            } catch (error) {
                console.log(error);
                this.showErrorSnackbar();
            } finally {
                this.deletingFaqModule = false;
            }
        },

        showErrorSnackbar(text = "An error has occurred") {
            this.snackbarOpen = true;
            this.snackbarColor = "error";
            this.snackbarText = text;
        },
    },
    mounted() {
        this.visibleBreadcrumbs = [this.breadcrumbs.dashboard];
        this.fetchModules();
    },
};
</script>
